import { FC } from 'react';
import { useEffect, useState } from 'react';
import { Box, Flex, HStack, Text } from '@chakra-ui/layout';
import { Button, IconButton } from '@chakra-ui/button';
import { AiOutlineExpand } from 'react-icons/ai';
import { Tooltip, useMediaQuery } from '@chakra-ui/react';
import { FiInfo, FiMenu } from 'react-icons/fi';
import { FaCog, FaTwitter } from 'react-icons/fa';
import getName from 'helpers/nft/crucibleScene/getName';
import CrucibleScene from 'helpers/nft/crucibleScene/crucibleScene';
import { shuffleAddress } from 'utils/address';
import { useWeb3React } from 'hooks/web3';

type Props = {
  crucibleId: string;
  mintTimestamp: number;
  blockNumber: number;
  isLoading: boolean;
  scene: CrucibleScene;
  forceFullscreen: boolean;
  screenshotMode?: boolean;
  toggleFullscreen: () => void;
  toggleTraitsTable: () => void;
  toggleGraphicsOptions: () => void;
  toggleControls: () => void;
};

const TopBar: FC<Props> = ({
  crucibleId,
  mintTimestamp,
  blockNumber,
  isLoading,
  scene,
  forceFullscreen,
  screenshotMode,
  toggleFullscreen,
  toggleTraitsTable,
  toggleControls,
  toggleGraphicsOptions
}) => {
  const { chainId = 1 } = useWeb3React();
  const [isLargerThan1280] = useMediaQuery('(min-width: 1280px)');
  const [lidButtonText, setLidButtonText] = useState('Close Lid');

  const handleShareOnTwitter = (e: any) => {
    e.stopPropagation();
    chainId === 1
      ? window.open(
          `https://twitter.com/intent/tweet?text=${encodeURI(
            `${window.location.origin}/nft-scene/${shuffleAddress(
              crucibleId,
              'crucible'
            )}`
          )}`,
          '_blank',
          'noopener,noreferrer'
        )
      : window.open(
          `https://twitter.com/intent/tweet?text=${encodeURI(
            `${window.location.origin}/nft-scene/${shuffleAddress(
              crucibleId,
              'crucible'
            )}`
          )}?network=${chainId}`,
          '_blank',
          'noopener,noreferrer'
        );
  };

  useEffect(() => {
    if (scene.isLidOpen) setLidButtonText('Close Lid');
    else setLidButtonText('Open Lid');
    scene.ee.on('lid', (e) => {
      if (e.type === 'open') {
        setLidButtonText('Close Lid');
      } else {
        setLidButtonText('Open Lid');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  if (screenshotMode) return null;

  return (
    <Box
      m={4}
      px={3}
      py={2}
      left={0}
      right={0}
      borderRadius='md'
      bg='whiteAlpha.100'
      position='absolute'
      backdropFilter='blur(20px)'
    >
      <Flex justifyContent='space-between' alignItems='center'>
        <HStack>
          <Tooltip hasArrow label='Show/Hide traits'>
            <IconButton
              aria-label='traits'
              bg='whiteAlpha.100'
              onClick={toggleTraitsTable}
              icon={<FiMenu />}
              disabled={isLoading}
              variant='ghost'
              size='sm'
            />
          </Tooltip>

          <Tooltip hasArrow label='Show/Hide Controls'>
            <IconButton
              aria-label='control'
              bg='whiteAlpha.100'
              onClick={toggleControls}
              icon={<FiInfo />}
              disabled={isLoading}
              variant='ghost'
              size='sm'
            />
          </Tooltip>
        </HStack>
        {isLargerThan1280 && (
          <Box flexGrow={1}>
            <Text textAlign='center'>
              {getName(crucibleId, blockNumber, mintTimestamp)}
            </Text>
          </Box>
        )}
        <Box>
          <HStack>
            <Button
              aria-label='lock'
              bg='whiteAlpha.100'
              onClick={() => scene.openLidAnimation()}
              disabled={isLoading}
              variant='ghost'
              size='sm'
            >
              <Text fontSize='xs' fontWeight='400'>
                {lidButtonText}
              </Text>
            </Button>
            <IconButton
              aria-label='graphics options'
              bg='whiteAlpha.100'
              icon={<FaCog />}
              onClick={() => toggleGraphicsOptions()}
              disabled={isLoading}
              variant='ghost'
              size='sm'
            />
            {/* TODO: AR */}
            {/* <IconButton
              aria-label='AR'
              bg='whiteAlpha.100'
              icon={<AiOutlineCodeSandbox />}
              disabled={isLoading}
              variant='ghost'
              size='sm'
            /> */}
            {window === window.parent && (
              <>
                {forceFullscreen ? (
                  <IconButton
                    aria-label='share'
                    bg='whiteAlpha.100'
                    icon={<FaTwitter />}
                    onClick={handleShareOnTwitter}
                    disabled={isLoading}
                    variant='ghost'
                    size='sm'
                  />
                ) : (
                  <IconButton
                    aria-label='fullscreen'
                    bg='whiteAlpha.100'
                    icon={<AiOutlineExpand />}
                    onClick={() => toggleFullscreen()}
                    disabled={isLoading}
                    variant='ghost'
                    size='sm'
                  />
                )}
              </>
            )}
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default TopBar;
